import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import Cookies from "js-cookie";

interface LayoutState {
  projectSidebarCollapsed: boolean;
  currentProjectId: string | null;
  currentProjectName: string | null;
  currentProjectStatus: string | null;
  mobileMenuOpen: boolean;
  activeMainNavItem: string;
}

// Persist last-known project across hard refreshes / direct URL loads via
// cookies (project convention — see access-token in src/route/ProtectedRoute).
// Cleared on logout in src/layout/header/dropdown/user/User.js.
const PROJECT_ID_COOKIE = "currentProjectId";
const PROJECT_NAME_COOKIE = "currentProjectName";
const PROJECT_STATUS_COOKIE = "currentProjectStatus";

// Sidebar collapse is a pure UI preference with no server relevance — same
// class as localStorage["pxAppFontScale"] (src/theme/fontScale.tsx), so it uses
// localStorage rather than the project cookies above.
const SIDEBAR_COLLAPSED_KEY = "pmsProjectSidebarCollapsed";

const readStoredCollapsed = (): boolean => {
  try {
    return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === "1";
  } catch {
    return false;
  }
};

// Storage side-effects inside reducers are the existing convention in this
// slice (see the Cookies.set calls in setCurrentProject) — not an oversight.
const writeStoredCollapsed = (value: boolean) => {
  try {
    localStorage.setItem(SIDEBAR_COLLAPSED_KEY, value ? "1" : "0");
  } catch {
    /* private mode / storage disabled — preference just won't persist */
  }
};

const initialState: LayoutState = {
  projectSidebarCollapsed: readStoredCollapsed(),
  currentProjectId: Cookies.get(PROJECT_ID_COOKIE) || null,
  currentProjectName: Cookies.get(PROJECT_NAME_COOKIE) || null,
  currentProjectStatus: Cookies.get(PROJECT_STATUS_COOKIE) || null,
  mobileMenuOpen: false,
  activeMainNavItem: "dashboard",
};

const layoutSlice = createSlice({
  name: "layout",
  initialState,
  reducers: {
    toggleProjectSidebar: (state) => {
      state.projectSidebarCollapsed = !state.projectSidebarCollapsed;
      writeStoredCollapsed(state.projectSidebarCollapsed);
    },
    setProjectSidebarCollapsed: (state, action: PayloadAction<boolean>) => {
      state.projectSidebarCollapsed = action.payload;
      writeStoredCollapsed(action.payload);
    },
    setCurrentProject: (
      state,
      action: PayloadAction<{
        id: string | null;
        name: string | null;
        status?: string | null;
      }>
    ) => {
      const projectChanged = action.payload.id !== state.currentProjectId;
      state.currentProjectId = action.payload.id;
      state.currentProjectName = action.payload.name;
      if (action.payload.id) {
        Cookies.set(PROJECT_ID_COOKIE, action.payload.id);
      } else {
        Cookies.remove(PROJECT_ID_COOKIE);
      }
      if (action.payload.name) {
        Cookies.set(PROJECT_NAME_COOKIE, action.payload.name);
      } else {
        Cookies.remove(PROJECT_NAME_COOKIE);
      }
      if (action.payload.status !== undefined) {
        state.currentProjectStatus = action.payload.status;
        if (action.payload.status) {
          Cookies.set(PROJECT_STATUS_COOKIE, action.payload.status);
        } else {
          Cookies.remove(PROJECT_STATUS_COOKIE);
        }
      } else if (projectChanged) {
        // Project switched without a known status — don't show a stale pill
        state.currentProjectStatus = null;
        Cookies.remove(PROJECT_STATUS_COOKIE);
      }
    },
    clearCurrentProject: (state) => {
      state.currentProjectId = null;
      state.currentProjectName = null;
      state.currentProjectStatus = null;
      Cookies.remove(PROJECT_ID_COOKIE);
      Cookies.remove(PROJECT_NAME_COOKIE);
      Cookies.remove(PROJECT_STATUS_COOKIE);
    },
    toggleMobileMenu: (state) => {
      state.mobileMenuOpen = !state.mobileMenuOpen;
    },
    setMobileMenuOpen: (state, action: PayloadAction<boolean>) => {
      state.mobileMenuOpen = action.payload;
    },
    setActiveMainNavItem: (state, action: PayloadAction<string>) => {
      state.activeMainNavItem = action.payload;
    },
  },
});

export const {
  toggleProjectSidebar,
  setProjectSidebarCollapsed,
  setCurrentProject,
  clearCurrentProject,
  toggleMobileMenu,
  setMobileMenuOpen,
  setActiveMainNavItem,
} = layoutSlice.actions;

export default layoutSlice.reducer;
